import { useEffect, useMemo, useState } from "react";
import { Check, Copy, ExternalLink, Layers3, PanelRightOpen, SquareStack, MousePointerClick } from "lucide-react";
import { useApi } from "@/hooks/use-api";

type EmbedMode = "inline" | "popup" | "slider" | "standalone";

interface WorkspaceTenant {
  id: string;
  slug: string;
  name: string;
  status: string;
  plan: string;
  permissionLevel: string;
  accessSource: string;
}

interface WorkspaceContext {
  activeTenant: WorkspaceTenant | null;
  tenants: WorkspaceTenant[];
}

interface PublicBookableServiceSummary {
  id: string;
  name: string;
  durationMinutes: number;
  categoryLabel: string;
}

interface PublicBookingServicesPayload {
  services: PublicBookableServiceSummary[];
}

const MODE_META: Array<{
  mode: EmbedMode;
  label: string;
  description: string;
  Icon: typeof Layers3;
}> = [
  { mode: "inline", label: "Inline", description: "Render directly where the script tag is placed.", Icon: Layers3 },
  { mode: "popup", label: "Popup", description: "Show a floating CTA that opens a centered modal.", Icon: MousePointerClick },
  { mode: "slider", label: "Slider", description: "Open the booking runtime from the right edge.", Icon: PanelRightOpen },
  { mode: "standalone", label: "Standalone", description: "Send visitors to the full booking page in a new tab.", Icon: SquareStack },
];

function buildScriptUrl(baseUrl: string): string {
  return `${baseUrl.replace(/\/$/, "")}/embed/booking.js`;
}

function buildEmbedCode({
  baseUrl,
  tenantSlug,
  serviceId,
  mode,
  title,
  buttonLabel,
}: {
  baseUrl: string;
  tenantSlug: string;
  serviceId: string;
  mode: EmbedMode;
  title: string;
  buttonLabel: string;
}): string {
  const attrs = [
    `src="${buildScriptUrl(baseUrl)}"`,
    `data-tenant="${tenantSlug}"`,
    `data-mode="${mode}"`,
    `data-title="${title}"`,
    `data-button-label="${buttonLabel}"`,
  ];

  if (serviceId.trim()) {
    attrs.push(`data-service-id="${serviceId.trim()}"`);
  }

  if (mode === "inline") {
    attrs.push(`data-min-height="760"`);
  }

  return `<script ${attrs.join(" ")}></script>`;
}

function buildPreviewDocument(code: string): string {
  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body {
        margin: 0;
        padding: 24px;
        font-family: Inter, ui-sans-serif, system-ui, sans-serif;
        background:
          radial-gradient(circle at top left, rgba(20, 184, 166, 0.10), transparent 36%),
          linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
        color: #0f172a;
      }
      .preview-shell {
        max-width: 960px;
        margin: 0 auto;
        display: grid;
        gap: 18px;
      }
      .preview-card {
        border: 1px solid rgba(148, 163, 184, 0.24);
        border-radius: 24px;
        background: rgba(255, 255, 255, 0.84);
        box-shadow: 0 24px 60px rgba(15, 23, 42, 0.10);
        padding: 24px;
      }
      .preview-kicker {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.24em;
        color: #0f766e;
        font-weight: 700;
      }
      .preview-title {
        margin: 10px 0 0;
        font-size: 30px;
        line-height: 1.1;
      }
      .preview-copy {
        margin: 12px 0 0;
        color: #475569;
        max-width: 60ch;
      }
      .preview-mount {
        min-height: 240px;
      }
    </style>
  </head>
  <body>
    <div class="preview-shell">
      <section class="preview-card">
        <p class="preview-kicker">Booking widget preview</p>
        <h1 class="preview-title">Live embed runtime</h1>
        <p class="preview-copy">
          This preview loads the real mounted booking widget script, so popup, slider, and standalone modes are exercised against the same runtime third-party sites receive.
        </p>
      </section>
      <section class="preview-card preview-mount">
        ${code}
      </section>
    </div>
  </body>
</html>`;
}

export default function BookingEmbedPage() {
  const [mode, setMode] = useState<EmbedMode>("inline");
  const [tenantSlug, setTenantSlug] = useState("acme");
  const [tenantSlugTouched, setTenantSlugTouched] = useState(false);
  const [serviceId, setServiceId] = useState("");
  const [baseUrl, setBaseUrl] = useState(() => (typeof window !== "undefined" ? window.location.origin : "https://app.stero.ai"));
  const [title, setTitle] = useState("Book with Stero");
  const [buttonLabel, setButtonLabel] = useState("Book now");
  const [copied, setCopied] = useState(false);
  const {
    data: workspace,
    isLoading: isLoadingWorkspace,
    error: workspaceError,
  } = useApi<WorkspaceContext>("/v1/platform/workspace-context");
  const activeTenant = workspace?.activeTenant ?? null;
  const servicesPath = activeTenant?.id
    ? `/api/public/bookings/services?tenant_id=${encodeURIComponent(activeTenant.id)}`
    : "";
  const {
    data: publicServices,
    isLoading: isLoadingServices,
    error: servicesError,
  } = useApi<PublicBookingServicesPayload>(servicesPath, { enabled: Boolean(activeTenant?.id) });
  const services = publicServices?.services ?? [];

  useEffect(() => {
    if (!activeTenant?.slug || tenantSlugTouched) {
      return;
    }

    setTenantSlug(activeTenant.slug);
  }, [activeTenant?.slug, tenantSlugTouched]);

  const code = useMemo(
    () => buildEmbedCode({ baseUrl, tenantSlug, serviceId, mode, title, buttonLabel }),
    [baseUrl, tenantSlug, serviceId, mode, title, buttonLabel],
  );

  const previewDoc = useMemo(() => buildPreviewDocument(code), [code]);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--color-primary)]">Booking embed</p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-[var(--color-foreground)]">
          Embeddable booking widget
        </h1>
        <p className="mt-3 text-sm leading-6 text-[var(--color-muted-foreground)]">
          Generate the real third-party booking snippet, switch between inline, popup, slider, and standalone modes, and preview the mounted runtime before handing it to a site owner.
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <section className="space-y-6 rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6 shadow-sm">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {MODE_META.map(({ mode: candidate, label, description, Icon }) => (
              <button
                key={candidate}
                type="button"
                onClick={() => setMode(candidate)}
                className={`rounded-2xl border p-4 text-left transition-colors ${
                  mode === candidate
                    ? "border-[var(--color-primary)] bg-[var(--color-primary)]/8"
                    : "border-[var(--color-border)] hover:border-[var(--color-primary)]/30"
                }`}
              >
                <Icon className={`h-4 w-4 ${mode === candidate ? "text-[var(--color-primary)]" : "text-[var(--color-muted-foreground)]"}`} />
                <p className="mt-3 text-sm font-medium text-[var(--color-foreground)]">{label}</p>
                <p className="mt-1 text-xs leading-5 text-[var(--color-muted-foreground)]">{description}</p>
              </button>
            ))}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Widget host URL</span>
              <input
                value={baseUrl}
                onChange={(event) => setBaseUrl(event.target.value)}
                className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2.5 text-sm text-[var(--color-foreground)]"
              />
            </label>
            <label className="space-y-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Tenant slug</span>
              <input
                value={tenantSlug}
                onChange={(event) => {
                  setTenantSlugTouched(true);
                  setTenantSlug(event.target.value);
                }}
                className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2.5 text-sm text-[var(--color-foreground)]"
              />
              <span className="block text-xs leading-5 text-[var(--color-muted-foreground)]">
                {isLoadingWorkspace
                  ? "Loading active workspace tenant..."
                  : workspaceError
                    ? workspaceError
                    : activeTenant
                      ? `Using active tenant ${activeTenant.name} (${activeTenant.slug}).`
                      : "No active tenant found; enter a tenant slug manually."}
              </span>
            </label>
            <label className="space-y-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Optional public service</span>
              <select
                value={serviceId}
                onChange={(event) => setServiceId(event.target.value)}
                className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2.5 text-sm text-[var(--color-foreground)]"
              >
                <option value="">All public services</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name} · {service.categoryLabel} · {service.durationMinutes} min
                  </option>
                ))}
              </select>
              <span className="block text-xs leading-5 text-[var(--color-muted-foreground)]">
                {isLoadingServices
                  ? "Loading services from /api/public/bookings/services..."
                  : servicesError
                    ? servicesError
                    : services.length > 0
                      ? "Service options come from the mounted public booking services contract."
                      : "Leave blank to show the full public service selector."}
              </span>
            </label>
            <label className="space-y-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Widget title</span>
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2.5 text-sm text-[var(--color-foreground)]"
              />
            </label>
          </div>

          <label className="space-y-2 text-sm">
            <span className="font-medium text-[var(--color-foreground)]">Button label</span>
            <input
              value={buttonLabel}
              onChange={(event) => setButtonLabel(event.target.value)}
              className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2.5 text-sm text-[var(--color-foreground)]"
            />
          </label>

          <div className="rounded-[24px] border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-[var(--color-foreground)]">Embed code</p>
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">This snippet loads the mounted widget runtime with the selected display mode.</p>
              </div>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
              >
                {copied ? <Check className="h-4 w-4 text-[var(--color-success)]" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied" : "Copy snippet"}
              </button>
            </div>
            <pre className="mt-4 overflow-x-auto rounded-2xl bg-[var(--color-background)] p-4 text-xs leading-6 text-[var(--color-foreground)]">
              {code}
            </pre>
          </div>
        </section>

        <section className="space-y-4 rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-[var(--color-foreground)]">Live preview</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">Preview uses the real widget script, not a local mock.</p>
            </div>
            <a
              href={`${baseUrl.replace(/\/$/, "")}/book/${encodeURIComponent(tenantSlug.trim() || "acme")}${serviceId.trim() ? `?serviceId=${encodeURIComponent(serviceId.trim())}` : ""}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-[var(--color-primary)] hover:underline"
            >
              Open booking page
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>

          <iframe
            title="Booking widget preview"
            srcDoc={previewDoc}
            className="h-[780px] w-full rounded-[24px] border border-[var(--color-border)] bg-white"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        </section>
      </div>
    </div>
  );
}
